import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { hasAnyRole } from "../auth/authUi";
import { CMMS_FIELD_ROLES } from "../features/cmms/cmmsRoles";
import { useIsCmmsEnabled } from "../features/cmms/cmmsFeature";
import { fetchToolServiceHistory } from "../fetchers/cmmsToolMonitoring";
import { useLocalStorage } from "./useLocalStorage";

/*
 * The service log for one tool, newest first.
 *
 * Shaped after useToolServiceStatuses, including both of its gates: the module switch so an OEE
 * install without CMMS never asks, and the role check because the endpoint is Cmms_Field. On
 * /tools the role gate can never fail (the route is [1,2] and both are in the policy), but the
 * hook is written to be mountable somewhere wider — the panel it feeds is presentational
 * precisely so it can be, and the technician who performs the service cannot reach /tools.
 *
 * `toolId` gates the request rather than defaulting to anything. There is no company-wide form
 * of this read, so a null tool means "ask nothing", not "ask about everything" — the trap
 * useToolServiceStatuses documents for its own omitted id.
 */
export function useToolServiceHistory(toolId, { take } = {}) {
  const { roleIds } = useAuth();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const cmmsEnabled = useIsCmmsEnabled();

  const isCmmsUser = hasAnyRole(roleIds, CMMS_FIELD_ROLES);

  const query = useQuery(
    ["cmmsToolServiceHistory", companyKey, toolId ?? null, take ?? null],
    () => fetchToolServiceHistory(companyKey, toolId, take),
    {
      enabled: Boolean(toolId) && cmmsEnabled && isCmmsUser && Boolean(companyKey),
      // The log only changes when a work order is closed, which is not something that happens
      // while someone reads this panel. Same minute as the status read next door.
      staleTime: 60000,
    },
  );

  return {
    entries: query.data ?? [],
    // isInitialLoading, not isLoading: a disabled query stays status:"loading" forever in
    // react-query v4, and this one is disabled every moment no tool is selected — which is
    // most of them. isLoading would leave the panel showing a skeleton for a closed drawer.
    isLoading: query.isInitialLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

export default useToolServiceHistory;
